import React, { useState } from 'react';
import { Settings, Shield, Mic, Monitor, Lock, Wifi, Download, ShieldCheck, BrainCircuit, Cpu, NotepadText, Star, NotebookPen, Check, CheckCheck, Users } from 'lucide-react';
import './profile-creation.scss';
import { useTranslation } from 'react-i18next';

const ProfileCreationGuide = () => {
	const [selectedEntity, setSelectedEntity] = useState(null);
	const [selectedProctoring, setSelectedProctoring] = useState(null);
	const [showProctoring, setShowProctoring] = useState(false);
	const [downloadSpeed, setDownloadSpeed] = useState(1.0);
	const [uploadSpeed, setUploadSpeed] = useState(0.7);
	const [ram, setRam] = useState(4);
	const [audioLevel, setAudioLevel] = useState(0.15);
	const [showDescription, setShowDescription] = useState(false);
	const { t } = useTranslation();

	const entities = [
		{
			id: 'recording_options',
			name: 'recording_options',
			icon: <Monitor className="entity-icon" />,
			category: 'Recording',
			description:
				'recording_option_profile',
			features: ['record_room', 'mobile_proctoring', 'track_location', 'record_video', 'record_screen', 'record_audio'],
			securityLevel: 'High'
		},
		{
			id: 'lockdown_options',
			name: 'lockdown_options',
			icon: <Lock className="entity-icon" />,
			category: 'Lock Down',
			description:
				'lockdown_options_profile',
			features: [
				'force_full_screen',
				'disable_right_click',
				'detect_resizing_of_window',
				'disable_clipboard',
				'disable_printing',
				'camera_view',
				'notify',
				'force_closure',
				'detect_unfocus',
				'disable_keyboard_shortcuts',
				'pause_and_resume',
				'allow_incognito_mode',
				'disable_tampering',
				'disable_highlight_text'
			],
			securityLevel: 'Medium'
		},
		{
			id: 'verification_options',
			name: 'verification_options',
			icon: <ShieldCheck className="entity-icon" />,
			category: 'Verification',
			description: 'verification_options_profile',
			features: ['verify_id', 'verify_candidate', 'verify_desktop', 'verify_multiple_devices'],
			securityLevel: 'High'
		},
		{
			id: 'artificial_intelligence',
			name: 'artificial_intelligence',
			icon: <BrainCircuit className="entity-icon" />,
			category: 'AI',
			description: 'artificial_intelligence_options',
			features: ['multiple_people', 'person_missing', 'object_detection'],
			securityLevel: 'Medium'
		},
		{
			id: 'system_options',
			name: 'system_options',
			icon: <Cpu className="entity-icon" />,
			category: 'System',
			description: 'system_options_profile',
			features: ['verify_upload_speed', 'verify_ram', 'verify_download_speed'],
			securityLevel: 'Medium'
		},
		{
			id: 'quiz_tools',
			name: 'quiz_tools',
			icon: <NotepadText className="entity-icon" />,
			category: 'Quiz Tools',
			description: 'quiz_tools_profile',
			features: ['chat_bot', 'live_chat'],
			securityLevel: 'Medium'
		}
	];

	const proctoring = [
		{
			id: 'recommended',
			name: 'recommended',
			icon: <Star className="entity-icon" />,
			category: 'recommended',
			description: 'behavior_settings_recommended',
			features: [
				{ heading: 'navigating_away', high: 2, medium: 1, low: 0 },
				{ heading: 'copy_paste_cut', high: 2, medium: 1, low: 0 },
				{ heading: 'browser_resized', high: 2, medium: 1, low: 0 },
				{ heading: 'object_detection', high: 2, medium: 1, low: 0 },
				{ heading: 'person_missing', high: 2, medium: 1, low: 0 },
				{ heading: 'multiple_people', high: 2, medium: 1, low: 0 }
			],
			securityLevel: 'High'
		},
		{
			id: 'group_exam',
			name: 'group_exam',
			icon: <Users className="entity-icon" />,
			category: 'Group Exam',
			description: 'allows_multiple_test_takers',
			features: [
				{ heading: 'navigating_away', high: 5, medium: 2, low: 0 },
				{ heading: 'copy_paste_cut', high: 9, medium: 4, low: 0 },
				{ heading: 'browser_resized', high: 3, medium: 1, low: 0 },
				{ heading: 'object_detection', high: 2, medium: 1, low: 0 },
				{ heading: 'person_missing', high: 9, medium: 1, low: 0 },
				{ heading: 'multiple_people', high: 7, medium: 1, low: 0 }
			],
			securityLevel: 'Medium'
		},
		{
			id: 'open_note',
			name: 'open_note',
			icon: <NotebookPen className="entity-icon" />,
			category: 'open_note',
			description: 'allows_other_applications',
			features: [
				{ heading: 'navigating_away', high: 4 },
				{ heading: 'copy_paste_cut', high: 4 },
				{ heading: 'browser_resized', high: 3 },
				{ heading: 'object_detection', high: 2 },
				{ heading: 'person_missing', high: 10 },
				{ heading: 'multiple_people', high: 5 }
			],
			securityLevel: 'High'
		},
		{
			id: 'lenient',
			name: 'lenient',
			icon: <Check className="entity-icon" />,
			category: 'Lenient',
			description: 'multiple_behavior_violations',
			features: [
				{ heading: 'navigating_away', high: 10 },
				{ heading: 'copy_paste_cut', high: 5 },
				{ heading: 'browser_resized', high: 10 },
				{ heading: 'object_detection', high: 10 },
				{ heading: 'person_missing', high: 10 },
				{ heading: 'multiple_people', high: 10 }
			],
			securityLevel: 'Medium'
		},
		{
			id: 'moderate',
			name: 'moderate',
			icon: <CheckCheck className="entity-icon" />,
			category: 'Moderate',
			description: 'some_behavior_violations',
			features: [
				{ heading: 'navigating_away', high: 5 },
				{ heading: 'copy_paste_cut', high: 5 },
				{ heading: 'browser_resized', high: 5 },
				{ heading: 'object_detection', high: 5 },
				{ heading: 'person_missing', high: 5 },
				{ heading: 'multiple_people', high: 5 }
			],
			securityLevel: 'Medium'
		},
		{
			id: 'strict',
			name: 'strict',
			icon: <Settings className="entity-icon" />,
			category: 'Strict',
			description: 'no_behavior_violations',
			features: [
				{ heading: 'navigating_away', high: 1 },
				{ heading: 'copy_paste_cut', high: 1 },
				{ heading: 'browser_resized', high: 1 },
				{ heading: 'object_detection', high: 1 },
				{ heading: 'person_missing', high: 1 },
				{ heading: 'multiple_people', high: 1 }
			],
			securityLevel: 'Medium'
		}
	];

	const handleEntitySelect = (entity) => {
		setSelectedEntity(entity);
		setShowDescription(true);
	};

	const handleProctoringSelect = (proctor) => {
		setSelectedProctoring(proctor);
		setShowProctoring(true);
	}

	const getDownloadColor = (speed) => {
		if (speed < 1) return '#ef4444';
		if (speed < 2) return '#f59e0b';
		return '#10b981';
	};

	const getUploadColor = (speed) => {
		if (speed < 0.7) return '#ef4444';
		if (speed < 1.5) return '#f59e0b';
		return '#10b981';
	};

	const getRamColor = (ramSize) => {
		if (ramSize < 4) return '#ef4444';
		if (ramSize < 8) return '#f59e0b';
		return '#10b981';
	};

	const getAudioColor = (audioLvl) => {
		if (audioLvl < 0.1) return '#ef4444';
		if (audioLvl < 0.25) return '#f59e0b';
		return '#10b981';
	};

	const getProgressPercentage = (value, min, max) => {
		if (max === min) return 0;
		const percentage = ((value - min) / (max - min)) * 100;
		return Math.min(100, Math.max(0, percentage));
	};

	const renderSliderMarks = (marks, min, max, formatLabel, keyMarks = []) => (
		<div className="speed-marks">
			{marks.map((mark, index) => {
				const isEdge = index === 0 || index === marks.length - 1;
				const isKey = keyMarks.includes(mark);
				const itemClass = [
					'speed-marks__item',
					isKey ? 'speed-marks__item--key' : '',
					!isEdge && !isKey ? 'speed-marks__item--secondary' : '',
					index === 0 ? 'speed-marks__item--start' : '',
					index === marks.length - 1 ? 'speed-marks__item--end' : '',
				].filter(Boolean).join(' ');

				return (
					<span
						key={mark}
						className={itemClass}
						style={{ left: `${getProgressPercentage(mark, min, max)}%` }}
					>
						<span className="speed-marks__full">{formatLabel(mark)}</span>
						<span className="speed-marks__short">{mark}</span>
					</span>
				);
			})}
		</div>
	);

	// Labels match the Minimum / Recommended / Optimal cards under each slider
	const getDownloadLabel = (speed) => {
		if (speed < 1) return t('minimum');
		if (speed < 2) return t('recommended');
		return t('optimal');
	};

	const getUploadLabel = (speed) => {
		if (speed < 0.7) return t('minimum');
		if (speed < 1.5) return t('recommended');
		return t('optimal');
	};

	const getRamLabel = (ramSize) => {
		if (ramSize < 4) return t('minimum');
		if (ramSize < 8) return t('recommended');
		return t('optimal');
	};

	const getAudioLabel = (audioLvl) => {
		if (audioLvl < 0.1) return t('minimum');
		if (audioLvl < 0.25) return t('recommended');
		return t('optimal');
	};

	return (
		<div className="profile-creation-guide">
			<div className="profile-creation-guide__container">
				<div className="profile-creation-guide__header">
					<h1 className="profile-creation-guide__title">{t('profile_creation_guide')}</h1>
					<p className="profile-creation-guide__description">{t('profile_creation_desc')}</p>
				</div>

				<div className="content-section">
					<h2 className="section-title">
						<Shield className="title-icon" />
						{t('what_are_entities')}
					</h2>

					<div className="info-box info-box--primary">
						<h3 className="info-title">{t('understanding_entities')}</h3>
						<p className="info-text">
							{t('understanding_entities_desc')}
						</p>
						<ul className="info-list">
							<li><strong>{t('recording_entities')}</strong> {t('recording_entities_desc')}</li>
							<li><strong>{t('lock_down_entities')}</strong> {t('lock_down_entities_desc')}</li>
							<li><strong>{t('verification_entities')}</strong> {t('verification_entities_desc')}</li>
							<li><strong>{t('ai_entities')}</strong> {t('ai_entities_desc')}</li>
							<li><strong>{t('system_entities')}</strong> {t('system_entities_desc')}</li>
							<li><strong>{t('quiz_entities')}</strong> {t('quiz_entities_desc')}</li>
						</ul>
					</div>

					<div className="info-box info-box--warning">
						<h3 className="info-title">{t('how_profiles_work')}</h3>
						<p className="info-text">
							{t('how_profiles_work_desc')}
						</p>
					</div>
				</div>

				<div className="content-section">
					<h2 className="section-title">{t('try_selecting_an_option')}</h2>
					<p className="section-description">{t('try_selecting_an_option_desc')}</p>

					<div className="entities-grid">
						{entities.map((entity) => (
							<div
								key={entity.id}
								onClick={() => handleEntitySelect(entity)}
								className={`entity-card ${selectedEntity?.id === entity.id ? 'entity-card--selected' : ''}`}
							>
								<div className="entity-card-content">
									<div className="entity-icon-container">
										{entity.icon}
									</div>
									<h3 className="entity-name">{t(entity.name)}</h3>
								</div>
							</div>
						))}
					</div>

					{showDescription && selectedEntity && (
						<div className="entity-description">
							<div className="entity-description-content">
								<div className="entity-description-icon">
									{selectedEntity.icon}
								</div>
								<div className="entity-description-text">
									<h3 className="entity-description-title">{t(selectedEntity.name)}</h3>
									<p className="entity-description-summary">{t(selectedEntity.description)}</p>

									<div className="entity-details">
										<div className="entity-features">
											<h4 className="details-title">{t('key_features')}</h4>
											<ul className="features-list">
												{selectedEntity.features.map((feature, index) => (
													<li key={index} className="feature-item">
														<div className="feature-dot"></div>
														{t(feature)}
													</li>
												))}
											</ul>
										</div>
										<div className="entity-security">
											<h4 className="details-title">{t('security_impact')}</h4>
											<div className="security-info">
												<span className="entity-category">• {selectedEntity.category} Entity</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="content-section">
					<h2 className="section-title">
						<Wifi className="title-icon" />
						{t('proctoring_settings')}
					</h2>

					<div className="info-box info-box--info">
						<h3 className="info-title">{t('system_requirements')}</h3>
						<p className="info-text">
							{t('system_requirements_desc')}
						</p>
					</div>

					<div className="speed-setting">
						<div className="speed-header">
							<Download className="speed-icon" />
							<h3 className="speed-title">{t('download_speed_requirement')}</h3>
						</div>

						<div className="speed-control">
							<div className="speed-display">
								<span className="speed-label">{t('current_setting')}</span>
								<span className="speed-value">
									{downloadSpeed.toFixed(1)} Mbps ({getDownloadLabel(downloadSpeed)})
								</span>
							</div>

							<div className="speed-slider-container">
								<div className="speed-slider-wrapper">
									<input
										type="range"
										min="0.5"
										max="4"
										step="0.1"
										value={downloadSpeed}
										onChange={(e) => setDownloadSpeed(parseFloat(e.target.value))}
										className="speed-slider"
										style={{
											'--slider-progress': `${getProgressPercentage(downloadSpeed, 0.5, 4)}%`,
											'--slider-fill': getDownloadColor(downloadSpeed),
										}}
									/>
								</div>
								{renderSliderMarks([0.5, 1.0, 2.0, 3.0, 4.0], 0.5, 4, (v) => `${v.toFixed(1)} Mbps`, [1.0])}
							</div>
						</div>

						<div className="speed-levels">
							<div className="speed-level speed-level--minimum">
								<h4 className="level-title">{t('minimum')}: 0.5 Mbps</h4>
								<p className="level-description">{t('basic_functionality_only')}</p>
							</div>
							<div className="speed-level speed-level--recommended">
								<h4 className="level-title">{t('recommended')}: 1.0 Mbps</h4>
								<p className="level-description">{t('smooth_operation_expected')}</p>
							</div>
							<div className="speed-level speed-level--optimal">
								<h4 className="level-title">{t('optimal')}: 2.0+ Mbps</h4>
								<p className="level-description">{t('best_performance_guaranteed')}</p>
							</div>
						</div>

						<div className="speed-explanation">
							<h4 className="explanation-title">{t('why_download_speed_matters')}</h4>
							<p className="explanation-text">
								{t('why_download_speed_matters_desc')}
							</p>
							<ul className="explanation-list">
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('low_speed')} (0.5-1.0 Mbps):</strong> {t('may_cause_loading_delays_affect_video_quality')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('medium_speed')} (1.0-2.0 Mbps):</strong> {t('good_for_most_exam_types')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('high_speed')} (2.0+ Mbps):</strong> {t('high_speed_desc')}</span>
								</li>
							</ul>
						</div>
					</div>

					<div className="speed-setting">
						<div className="speed-header">
							<Wifi className="speed-icon" />
							<h3 className="speed-title">{t('upload_speed_requirement')}</h3>
						</div>

						<div className="speed-control">
							<div className="speed-display">
								<span className="speed-label">{t('current_setting')}</span>
								<span className="speed-value">
									{uploadSpeed.toFixed(1)} Mbps ({getUploadLabel(uploadSpeed)})
								</span>
							</div>

							<div className="speed-slider-container">
								<div className="speed-slider-wrapper">
									<input
										type="range"
										min="0.3"
										max="3"
										step="0.1"
										value={uploadSpeed}
										onChange={(e) => setUploadSpeed(parseFloat(e.target.value))}
										className="speed-slider"
										style={{
											'--slider-progress': `${getProgressPercentage(uploadSpeed, 0.3, 3)}%`,
											'--slider-fill': getUploadColor(uploadSpeed),
										}}
									/>
								</div>
								{renderSliderMarks([0.3, 0.7, 1.5, 2.0, 3.0], 0.3, 3, (v) => `${v.toFixed(1)} Mbps`, [0.7])}
							</div>
						</div>

						<div className="speed-levels">
							<div className="speed-level speed-level--minimum">
								<h4 className="level-title">{t('minimum')}: 0.3 Mbps</h4>
								<p className="level-description">{t('basic_video_upload_only')}</p>
							</div>
							<div className="speed-level speed-level--recommended">
								<h4 className="level-title">{t('recommended')}: 0.7 Mbps</h4>
								<p className="level-description">{t('good_quality_video_streaming')}</p>
							</div>
							<div className="speed-level speed-level--optimal">
								<h4 className="level-title">{t('optimal')}: 1.5+ Mbps</h4>
								<p className="level-description">{t('hd_video_and_audio_streaming')}</p>
							</div>
						</div>

						<div className="speed-explanation">
							<h4 className="explanation-title">{t('why_upload_speed_matters')}</h4>
							<p className="explanation-text">
								{t('why_upload_speed_matters_desc')}
							</p>
							<ul className="explanation-list">
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('low_speed')} (0.3-0.7 Mbps):</strong> {t('basic_video_quality_possible_buffering')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('medium_speed')} (0.7-1.5 Mbps):</strong> {t('good_video_quality_stable_streaming')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('high_speed')} (1.5+ Mbps):</strong> {t('hd_quality_multiple_streams_support')}</span>
								</li>
							</ul>
						</div>
					</div>

					<div className="speed-setting">
						<div className="speed-header">
							<Monitor className="speed-icon" />
							<h3 className="speed-title">{t('ram_requirement')}</h3>
						</div>

						<div className="speed-control">
							<div className="speed-display">
								<span className="speed-label">{t('current_setting')}</span>
								<span className="speed-value">
									{ram} GB ({getRamLabel(ram)})
								</span>
							</div>

							<div className="speed-slider-container">
								<div className="speed-slider-wrapper">
									<input
										type="range"
										min="2"
										max="32"
										step="2"
										value={ram}
										onChange={(e) => setRam(parseInt(e.target.value))}
										className="speed-slider"
										style={{
											'--slider-progress': `${getProgressPercentage(ram, 2, 32)}%`,
											'--slider-fill': getRamColor(ram),
										}}
									/>
								</div>
								{renderSliderMarks([2, 4, 8, 16, 32], 2, 32, (v) => `${v} GB`, [4])}
							</div>
						</div>

						<div className="speed-levels">
							<div className="speed-level speed-level--minimum">
								<h4 className="level-title">{t('minimum')}: 2 GB</h4>
								<p className="level-description">{t('basic_system_operation')}</p>
							</div>
							<div className="speed-level speed-level--recommended">
								<h4 className="level-title">{t('recommended')}: 4 GB</h4>
								<p className="level-description">{t('smooth_multitasking')}</p>
							</div>
							<div className="speed-level speed-level--optimal">
								<h4 className="level-title">{t('optimal')}: 8+ GB</h4>
								<p className="level-description">{t('excellent_performance')}</p>
							</div>
						</div>

						<div className="speed-explanation">
							<h4 className="explanation-title">{t('why_ram_matters')}</h4>
							<p className="explanation-text">
								{t('why_ram_matters_desc')}
							</p>
							<ul className="explanation-list">
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('low_ram')} (2-4 GB):</strong> {t('low_ram_desc')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('medium_ram')} (4-8 GB):</strong> {t('medium_ram_desc')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('high_ram')} (8+ GB):</strong> {t('high_ram_desc')}</span>
								</li>
							</ul>
						</div>
					</div>

					<div className="speed-setting">
						<div className="speed-header">
							<Mic className="speed-icon" />
							<h3 className="speed-title">{t('audio_sensitivity_level')}</h3>
						</div>

						<div className="speed-control">
							<div className="speed-display">
								<span className="speed-label">{t('current_setting')}</span>
								<span className="speed-value">
									{audioLevel.toFixed(2)} dB ({getAudioLabel(audioLevel)})
								</span>
							</div>

							<div className="speed-slider-container">
								<div className="speed-slider-wrapper">
									<input
										type="range"
										min="0.05"
										max="0.4"
										step="0.01"
										value={audioLevel}
										onChange={(e) => setAudioLevel(parseFloat(e.target.value))}
										className="speed-slider"
										style={{
											'--slider-progress': `${getProgressPercentage(audioLevel, 0.05, 0.4)}%`,
											'--slider-fill': getAudioColor(audioLevel),
										}}
									/>
								</div>
								{renderSliderMarks([0.05, 0.15, 0.25, 0.4], 0.05, 0.4, (v) => `${v.toFixed(2)} dB`, [0.15])}
							</div>
						</div>

						<div className="speed-levels">
							<div className="speed-level speed-level--minimum">
								<h4 className="level-title">{t('minimum')} 0.05 dB</h4>
								<p className="level-description">{t('very_low_sensitivity')}</p>
							</div>
							<div className="speed-level speed-level--recommended">
								<h4 className="level-title">{t('recommended')} 0.15 dB</h4>
								<p className="level-description">{t('balanced_detection')}</p>
							</div>
							<div className="speed-level speed-level--optimal">
								<h4 className="level-title">{t('optimal')} 0.25+ dB</h4>
								<p className="level-description">{t('high_sensitivity_detection')}</p>
							</div>
						</div>

						<div className="speed-explanation">
							<h4 className="explanation-title">{t('why_audio_level_matters')}</h4>
							<p className="explanation-text">
								{t('why_audio_level_matters_desc')}
							</p>
							<ul className="explanation-list">
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('low_sensitivity')} (0.05-0.1 dB):</strong> {t('low_sensitivity_desc')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('medium_sensitivity')} (0.1-0.25 dB):</strong> {t('detects_normal_conversation')}</span>
								</li>
								<li className="explanation-item">
									<div className="explanation-dot"></div>
									<span><strong>{t('high_sensitivity')} (0.25+ dB):</strong> {t('high_sensitivity_desc')}</span>
								</li>
							</ul>
						</div>
					</div>
				</div>

				<div className="content-section">
					<h2 className="section-title">{t('proctoring_behavior_setting')}</h2>
					<p className="section-description">{t('proctoring_behavior_setting_desc')}</p>

					<div className="entities-grid">
						{
							proctoring.map((entity) => (
								<div
									key={entity.id}
									onClick={() => handleProctoringSelect(entity)}
									className={`entity-card ${selectedProctoring?.id === entity.id ? 'entity-card--selected' : ''}`}
								>
									<div className="entity-card-content">
										<div className="entity-icon-container">
											{entity.icon}
										</div>
										<h3 className="entity-name">{t(entity.name)}</h3>
									</div>
								</div>
							))
						}
					</div>

					{
						showProctoring && selectedProctoring && (
							<div className="entity-description">
								<div className="entity-description-content">
									<div className="entity-description-icon">
										{selectedProctoring.icon}
									</div>
									<div className="entity-description-text">
										<h3 className="entity-description-title">{t(selectedProctoring.name)}</h3>
										<p className="entity-description-summary">{t(selectedProctoring.description)}</p>

										<div className="entity-details">
											<div className="entity-features">
												<h4 className="details-title">{t('browser_features')}</h4>
												<ul className="features-list">
													{selectedProctoring.features.slice(0, 3).map((feature, index) => (
														<li key={index} className="feature-item">
															<div className="feature-dot"></div>
															<span className="feature-label">{t(feature.heading)}</span>
														</li>
													))}
												</ul>
											</div>
											<div className="entity-security">
												<h4 className="details-title">{t('incident_level')}</h4>
												<div className="security-info">
													<ul className="features-list">
														{selectedProctoring.features.slice(0, 3).map((feature, index) => (
															<li key={index} className="feature-item">
																<div className="feature-dot" style={{ background: 'red' }}></div>
																<span className="feature-value">{feature.high} {t('times')}</span>
															</li>
														))}
													</ul>
												</div>
											</div>
										</div>

										<div className="entity-details">
											<div className="entity-features">
												<h4 className="details-title">{t('ai_features')}</h4>
												<ul className="features-list">
													{selectedProctoring.features.slice(3).map((feature, index) => (
														<li key={index} className="feature-item">
															<div className="feature-dot"></div>
															<span className="feature-label">{t(feature.heading)}</span>
														</li>
													))}
												</ul>
											</div>
											<div className="entity-security">
												<h4 className="details-title">{t('incident_level')}</h4>
												<div className="security-info">
													<ul className="features-list">
														{selectedProctoring.features.slice(3).map((feature, index) => (
															<li key={index} className="feature-item">
																<div className="feature-dot" style={{ background: 'red' }}></div>
																<span className="feature-value">{feature.high} {t('seconds')}</span>
															</li>
														))}
													</ul>
												</div>
											</div>
										</div>

									</div>
								</div>
							</div>
						)
					}
				</div>
			</div>
		</div>
	);
};

export default ProfileCreationGuide;